'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { gridIdToPath } from '@/lib/utils';
import type { LiveEvent } from '@/hooks/use-live-events';
import { openDeferredEventSource } from '@/lib/open-deferred-event-source';

/**
 * Seat data from live grid updates
 */
export interface LiveSeat {
  seatIndex: number;
  holder: string | null;
  selfAssessedPrice: string;
  balance: string;
  lastUpdateTime: number;
  isActive: boolean;
}

/**
 * Grid update event from SSE
 */
interface GridEvent {
  poolId: string;
  seats: LiveSeat[];
  timestamp: number;
  version: number;
  type: 'full' | 'diff';
  changedCount?: number;
}

/**
 * Live event from /api/live SSE (for optimistic updates)
 */
interface LiveActivityEvent {
  poolId?: string;
  seatId?: string; // Format: {poolId}-{seatIndex}
  seatIndex?: number;
  user?: string;
  newHolder?: string;
  oldHolder?: string;
  price?: string;
  newPrice?: string;
  eventType?: string;
}

/**
 * Extract seatIndex from seatId (format: {poolId}-{seatIndex})
 */
function extractSeatIndex(seatId: string | undefined): number | undefined {
  if (!seatId) return undefined;
  const parts = seatId.split('-');
  if (parts.length < 2) return undefined;
  const seatIndex = parseInt(parts[parts.length - 1], 10);
  return isNaN(seatIndex) ? undefined : seatIndex;
}

/**
 * Hook state
 */
interface UseLiveGridState {
  /** Current grid seats (merged with updates) */
  seats: LiveSeat[];
  /** Recent activity events for this pool (newest first, capped at ~50) */
  events: LiveEvent[];
  /** Whether initial connection is established */
  isConnected: boolean;
  /** Whether initial grid snapshot has been received */
  isLoaded: boolean;
  /** Last update timestamp */
  lastUpdate: number | null;
  /** Connection error if any */
  error: string | null;
  /** Number of updates received */
  updateCount: number;
}

const MAX_LIVE_EVENTS = 50;

/**
 * Hook to subscribe to real-time grid updates via SSE.
 * 
 * This hook connects to TWO SSE streams:
 * 1. /api/live/grid/[poolId] - Background grid polling (Envio data)
 * 2. /api/live?poolId=[poolId] - Instant event updates (optimistic)
 * 
 * When events come in on stream 2, we apply optimistic updates immediately,
 * then stream 1 will eventually sync with the full data.
 * 
 * @param poolId - The pool ID to subscribe to
 * @param enabled - Whether to enable the subscription (default: true)
 * @returns Live grid state and update handler
 */
export function useLiveGrid(
  poolId: string | undefined,
  enabled: boolean = true
): UseLiveGridState & { 
  /** Manually trigger a reconnection */
  reconnect: () => void;
  /** Apply a local optimistic update (e.g. after tx confirmation) */
  applyLocalUpdate: (seatIndex: number, holder: string | null, price: string) => void;
} {
  const [state, setState] = useState<UseLiveGridState>({
    seats: [],
    events: [],
    isConnected: false,
    isLoaded: false,
    lastUpdate: null,
    error: null,
    updateCount: 0,
  });

  // We now route opens through `openDeferredEventSource`, which returns
  // a close handler instead of the raw EventSource. The ref holds the
  // close handler so we can tear down on cleanup / manual reconnect.
  // (Previously this hook also held a `liveCloseRef` for a second
  // `/api/live?poolId=X` stream; that stream was collapsed into
  // `grid_events` on the existing grid SSE — see `connect()` below.)
  const gridCloseRef = useRef<(() => void) | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const seatsMapRef = useRef<Map<number, LiveSeat>>(new Map());

  // Tracks the `poolId` that the current `state` slice belongs to.
  // Cleared synchronously on `poolId` flips (see reset effect below) and
  // stamped back to `poolId` the moment the first SSE payload for that
  // pool lands. The public return short-circuits when the caller's
  // `poolId` doesn't match this ref, so consumers (`useGridPage` →
  // `mergedRawSeats` / `mergeRecentGridEvents`) never observe stale
  // seats / events from the previous board on the cross-render frame.
  const syncedPoolIdRef = useRef<string | undefined>(undefined);

  // Seats with recent optimistic/local updates are "protected" from being
  // overwritten by stale grid poll data for a grace period.
  const protectedSeatsRef = useRef<Map<number, number>>(new Map());
  const PROTECTION_GRACE_MS = 15_000;

  /** Check if a seat is still protected from grid poll overwrites. */
  const isSeatProtected = useCallback((seatIndex: number, incomingSeat: LiveSeat): boolean => {
    const protectedAt = protectedSeatsRef.current.get(seatIndex);
    if (!protectedAt) return false;
    if (Date.now() - protectedAt > PROTECTION_GRACE_MS) {
      protectedSeatsRef.current.delete(seatIndex);
      return false;
    }
    // Allow the overwrite if the grid data has caught up to the optimistic
    // state (same holder or a newer holder change).
    const currentSeat = seatsMapRef.current.get(seatIndex);
    if (currentSeat && incomingSeat.holder?.toLowerCase() === currentSeat.holder?.toLowerCase()) {
      protectedSeatsRef.current.delete(seatIndex);
      return false;
    }
    return true;
  }, []);

  /** Mark a seat as protected and update the seatsMap + state. */
  const protectAndUpdate = useCallback((seatIndex: number, seat: LiveSeat) => {
    protectedSeatsRef.current.set(seatIndex, Date.now());
    seatsMapRef.current.set(seatIndex, seat);
    setState((prev) => ({
      ...prev,
      seats: Array.from(seatsMapRef.current.values()),
      lastUpdate: Date.now(),
      updateCount: prev.updateCount + 1,
    }));
  }, []);

  // Apply optimistic update from live event
  const applyOptimisticUpdate = useCallback((event: LiveActivityEvent) => {
    // Extract seatIndex from seatId if not directly provided
    const seatIndex = event.seatIndex ?? extractSeatIndex(event.seatId);
    if (seatIndex === undefined) return;
    
    const existingSeat = seatsMapRef.current.get(seatIndex);
    
    // Determine new holder based on event type
    let newHolder: string | null = existingSeat?.holder || null;
    if (event.eventType === 'CLAIMED' || event.eventType === 'BOUGHT') {
      newHolder = event.newHolder || event.user || null;
    } else if (event.eventType === 'ABANDONED' || event.eventType === 'FORFEITED') {
      newHolder = null;
    }
    
    // Determine price.
    //   - PRICE_UPDATED / DEPOSIT_*: prefer the event's newPrice/price.
    //   - CLAIMED / BOUGHT: same — the event carries the buyer's price.
    //   - FORFEITED / ABANDONED on V22: `_clearSeat` deliberately
    //     PRESERVES `seat.price` as the Dutch auction decay anchor
    //     (UnifiedGridManagerV22.sol:_clearSeat + _dutchPrice). The
    //     indexer mirrors this — `SeatForfeited.handler` writes
    //     `selfAssessedPrice: isV22 ? oldPrice : 0n` and
    //     `SeatAbandoned.handler` no-ops on the price when the seat
    //     entered Dutch via SeatForfeited (forfeitedFrom == holder).
    //     Zeroing the price client-side here was the bug that surfaced
    //     as "$500 seat became $0.01" the instant pokeTax forfeited
    //     a tax-underwater holder: the chain still has the original
    //     price, but the optimistic update painted it as 0 until the
    //     next 15s React Query refetch. Preserving the previous
    //     price is correct for V22 (matches the chain anchor) and
    //     for V21 it briefly lags by one poll before the indexer
    //     resyncs to 0 — strictly better than the previous instant
    //     "$0" misreport.
    const isVacated = event.eventType === 'ABANDONED' || event.eventType === 'FORFEITED';
    const newPrice = isVacated
      ? (existingSeat?.selfAssessedPrice || event.newPrice || event.price || '0')
      : (event.newPrice || event.price || existingSeat?.selfAssessedPrice || '0');
    
    // Preserve the indexer's lastUpdateTime so client-side tax deduction
    // remains accurate. Resetting to Date.now() would zero out elapsed time
    // and inflate the displayed balance/runway.
    const holderChanged = existingSeat && existingSeat.holder?.toLowerCase() !== newHolder?.toLowerCase();
    const lastUpdateTime = holderChanged
      ? Math.floor(Date.now() / 1000)
      : (existingSeat?.lastUpdateTime || Math.floor(Date.now() / 1000));

    const updatedSeat: LiveSeat = {
      seatIndex,
      holder: newHolder,
      selfAssessedPrice: newPrice,
      balance: existingSeat?.balance || '0',
      lastUpdateTime,
      isActive: event.eventType !== 'ABANDONED' && event.eventType !== 'FORFEITED',
    };

    protectAndUpdate(seatIndex, updatedSeat);
  }, [protectAndUpdate]);

  /**
   * Apply a local optimistic update directly (called from drawers after tx success).
   * Protected from grid poll overwrites for 15 seconds.
   */
  const applyLocalUpdate = useCallback((seatIndex: number, holder: string | null, price: string) => {
    const existingSeat = seatsMapRef.current.get(seatIndex);
    const updatedSeat: LiveSeat = {
      seatIndex,
      holder,
      selfAssessedPrice: price,
      balance: existingSeat?.balance || '0',
      lastUpdateTime: Math.floor(Date.now() / 1000),
      isActive: holder !== null,
    };
    protectAndUpdate(seatIndex, updatedSeat);
  }, [protectAndUpdate]);

  const connect = useCallback(() => {
    if (!poolId || !enabled) return;

    gridCloseRef.current?.();
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Connect to grid SSE (background Envio polling). Deferred open
    // absorbs React StrictMode's dev double-mount (see
    // `openDeferredEventSource` for the rationale).
    const gridUrl = `/api/live/grid/${gridIdToPath(poolId)}`;
    gridCloseRef.current = openDeferredEventSource(gridUrl, (gridEventSource) => {
      gridEventSource.onopen = () => {
        setState((prev) => ({ ...prev, isConnected: true, error: null }));
      };

      gridEventSource.onerror = () => {
        setState((prev) => ({
          ...prev,
          isConnected: false,
          error: 'Connection lost. Reconnecting...',
        }));
        reconnectTimeoutRef.current = setTimeout(() => connect(), 5000);
      };

      gridEventSource.addEventListener('grid_snapshot', (event) => {
        try {
          const data: GridEvent = JSON.parse(event.data);
          // Preserve protected seats (recent optimistic/local updates) so stale
          // Envio data doesn't overwrite them.
          const preserved = new Map<number, LiveSeat>();
          for (const [idx, seat] of seatsMapRef.current) {
            if (protectedSeatsRef.current.has(idx)) {
              const incoming = data.seats.find(s => s.seatIndex === idx);
              if (!incoming || isSeatProtected(idx, incoming)) {
                preserved.set(idx, seat);
              }
            }
          }
          seatsMapRef.current.clear();
          for (const seat of data.seats) {
            if (!preserved.has(seat.seatIndex)) {
              seatsMapRef.current.set(seat.seatIndex, seat);
            }
          }
          for (const [idx, seat] of preserved) {
            seatsMapRef.current.set(idx, seat);
          }
          // Mark the synced pool *before* the setState so the very next
          // render observes the new state aligned with `poolId` and the
          // short-circuit at the bottom of the hook lets the data through.
          syncedPoolIdRef.current = poolId;
          setState((prev) => ({
            ...prev,
            seats: Array.from(seatsMapRef.current.values()),
            isLoaded: true,
            lastUpdate: data.timestamp,
            updateCount: prev.updateCount + 1,
          }));
        } catch (error) {
          console.error('[useLiveGrid] Failed to parse grid_snapshot:', error);
        }
      });

      // Handle differential updates from grid SSE
      gridEventSource.addEventListener('grid_update', (event) => {
        try {
          const data: GridEvent = JSON.parse(event.data);
          for (const seat of data.seats) {
            if (!isSeatProtected(seat.seatIndex, seat)) {
              seatsMapRef.current.set(seat.seatIndex, seat);
            }
          }
          // Defensive: a `grid_update` should never arrive before its
          // owning `grid_snapshot`, but if a cached SSE replay or future
          // server change ever reverses the ordering this keeps the
          // sync ref in lockstep with the data slice it represents.
          syncedPoolIdRef.current = poolId;
          setState((prev) => ({
            ...prev,
            seats: Array.from(seatsMapRef.current.values()),
            lastUpdate: data.timestamp,
            updateCount: prev.updateCount + 1,
          }));
        } catch (error) {
          console.error('[useLiveGrid] Failed to parse grid_update:', error);
        }
      });

      // `grid_events` rides the same connection as `grid_update` and
      // carries the `GridSeatEvent` rows the poll consumed this tick.
      // We use this single stream for THREE things, replacing what used
      // to be a separate `/api/live?poolId=X` `EventSource` per board:
      //
      //   1. Dedup + append into `state.events` so the chains /
      //      DrainShield visuals fire in lockstep with the seat diff —
      //      see the original race-fix rationale below.
      //   2. Drive `applyOptimisticUpdate` so a freshly-vacated /
      //      bought tile flips its holder + protected-overwrite flag
      //      without waiting for the next `grid_update` on the same
      //      tick. Previously `/api/live?poolId=X` did this; both
      //      streams are backed by the same Envio poll cycle so
      //      dropping one removes a TCP stream + accept-queue slot per
      //      board without changing observed latency.
      //   3. Free a SSE slot toward `MAX_SSE_PER_IP` (lib/sse-budget),
      //      which is the reason we collapsed: production was hitting
      //      a per-replica accept queue ceiling, with each board
      //      previously pinning 3 long-lived streams.
      //
      // Dedup by event id keeps the legacy `seat_forfeited` /
      // `seat_bought` channel idempotent if it ever comes back —
      // whichever stream lands first wins; the other is a no-op.
      gridEventSource.addEventListener('grid_events', (event) => {
        try {
          const data: { poolId?: string; events: Array<LiveEvent & LiveActivityEvent> } = JSON.parse(event.data);
          if (data.poolId?.toLowerCase() !== poolId.toLowerCase()) return;
          if (!Array.isArray(data.events) || data.events.length === 0) return;

          const receivedAt = Date.now();
          // Apply optimistic updates BEFORE the setState so the seats
          // map ref + protectedSeats set are warm by the time the
          // events-array reducer commits and downstream consumers
          // observe the merged snapshot.
          for (const ev of data.events) {
            if (!ev) continue;
            applyOptimisticUpdate(ev);
          }
          setState((prev) => {
            const seen = new Set(prev.events.map((e) => e.id));
            const incoming: LiveEvent[] = [];
            for (const raw of data.events) {
              if (!raw?.id || seen.has(raw.id)) continue;
              seen.add(raw.id);
              incoming.push({ ...raw, receivedAt });
            }
            if (incoming.length === 0) return prev;
            const merged = [...incoming, ...prev.events].slice(0, MAX_LIVE_EVENTS);
            return { ...prev, events: merged };
          });
        } catch (error) {
          console.error('[useLiveGrid] Failed to parse grid_events:', error);
        }
      });
    });

    // (Removed: the per-board `/api/live?poolId=X` connection. Its
    // duties — optimistic seat overlay + appending events into
    // `state.events` — are now handled inline by the `grid_events`
    // listener on the existing grid stream above. Dropping it cut
    // each board tab's SSE count from 3 to 2.)

  }, [poolId, enabled, applyOptimisticUpdate, isSeatProtected]);

  // Reset every per-pool slice of state + refs whenever `poolId` flips so
  // the next board never inherits the previous board's holders, live
  // events, or optimistic-update protections. Without this, switching
  // boards left the `seatsMapRef` populated with the outgoing grid's
  // holders until the new `grid_snapshot` SSE message arrived; in that
  // window `useGridPage`'s `mergedRawSeats` overlay would graft those
  // stale holders + their FORFEITED/protected flags onto the incoming
  // board's API seats, and `mergeRecentGridEvents` would re-stamp the
  // outgoing live events with the *new* gridId — surfacing as ghost
  // forfeit / protected pills on tiles that aren't actually in those
  // states on the freshly opened board. The hook used to be stable enough
  // that this slipped past review (the SSE snapshot would arrive within a
  // few hundred ms and overwrite the stale slice), but cached React Query
  // boards return `gridLoading=false` instantly — and the merge ran with
  // the old `liveSeats` for that whole gap. Resetting here closes the
  // window deterministically: the next render observes empty seats /
  // events for the new pool until live data actually arrives, which is
  // the truthful representation.
  useEffect(() => {
    seatsMapRef.current.clear();
    protectedSeatsRef.current.clear();
    // Drop the sync stamp synchronously alongside the state reset so the
    // very next render's short-circuit guard fires (the React state reset
    // is itself a `setState`, which lands in the same commit; the ref
    // write is what closes the cross-render window between this effect
    // running post-paint and the next snapshot SSE arriving).
    syncedPoolIdRef.current = undefined;
    setState({
      seats: [],
      events: [],
      isConnected: false,
      isLoaded: false,
      lastUpdate: null,
      error: null,
      updateCount: 0,
    });
  }, [poolId]);

  // Connect on mount / poolId change
  useEffect(() => {
    if (enabled && poolId) {
      connect();
    }

    return () => {
      gridCloseRef.current?.();
      gridCloseRef.current = null;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [poolId, enabled, connect]);

  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  // Cross-render leak guard. The `poolId` reset effect runs *after*
  // commit + paint, so the first render where `poolId` flips from A to
  // B observes `state.seats` / `state.events` still bound to A. If we
  // returned that, `useGridPage` would overlay A's live data on B's API
  // seats (`mergedRawSeats`) and re-stamp A's events with B's gridId in
  // `mergeRecentGridEvents`, briefly painting A's forfeit / DrainShield
  // visuals on B's tiles. The ref-stamp dance above keeps
  // `syncedPoolIdRef.current` aligned with whichever pool the state slice
  // belongs to:
  //
  //   - reset effect → `undefined` (no pool currently synced)
  //   - first `grid_snapshot` for the new pool → `poolId`
  //   - subsequent `grid_update` keeps it in lockstep
  //
  // When the caller's `poolId` doesn't match the synced ref we return
  // an empty/loading slice so consumers' "live not loaded yet" branches
  // fire (which is the truthful representation: we genuinely have no
  // live data for this pool yet).
  if (poolId !== syncedPoolIdRef.current) {
    return {
      seats: [],
      events: [],
      isConnected: false,
      isLoaded: false,
      lastUpdate: null,
      error: null,
      updateCount: 0,
      reconnect,
      applyLocalUpdate,
    };
  }

  return { ...state, reconnect, applyLocalUpdate };
}

/**
 * Hook to get a single seat from the live grid.
 * Uses the shared grid subscription to avoid duplicate connections.
 */
export function useLiveSeat(
  poolId: string | undefined,
  seatIndex: number,
  enabled: boolean = true
): { seat: LiveSeat | null; isLoaded: boolean } {
  const { seats, isLoaded } = useLiveGrid(poolId, enabled);

  const seat = seats.find((s) => s.seatIndex === seatIndex) || null;

  return { seat, isLoaded };
}
